<template>
    <b-row>
        <b-col>
            <card title="Patient Allergy Updates">
                <div v-for="(allergy, index) in filteredAllergyUpdates" :key="allergy.id">
                    <card style="cursor:pointer" @click.native="toggleInfo(allergy)">
                        <b>{{ allergy.patient.lastName + ', ' + allergy.patient.firstName }}</b> - Submitted {{
                            getAge(allergy.submitted) }}
                    </card>
                    <b-collapse :id="'panel-' + allergy.id.toString()" visible.native="0">
                        <card>
                            <b-row class="mt-2" v-if="shouldLoad(allergy)">
                                <b-col cols="5">
                                    <card plain>
                                        <b>Patient Submitted Update</b><br /><br />
                                        <span style="white-space: pre-wrap;">{{ allergy.allergyUpdate }}</span>
                                        <hr />
                                        <div class="d-flex">
                                            <div class="flex-fill">
                                                <b-button variant="success" class="px-5"
                                                    @click="processAllergy(patientsAllergies[calculatedIndex(index)], allergy.id)">
                                                    <strong>Processed</strong>
                                                </b-button>
                                            </div>
                                            <div>
                                                <b-button
                                                    :to="{ name: 'PatientDashboard', params: { id: allergy.patient.id } }"
                                                    target="_blank" variant="info">
                                                    <i class="fa fa-user" />
                                                    Go to Profile
                                                </b-button>
                                            </div>
                                        </div>
                                    </card>
                                    <card plain>
                                        <PatientDemographicsComponent :value="allergy.patient" />
                                    </card>
                                </b-col>
                                <b-col cols="7">
                                    <card plain>
                                        <b>Patient Allergy Management</b>
                                        <PatientAllergyComponent v-model="patientsAllergies[calculatedIndex(index)]"
                                            :patient="allergy.patient" />
                                    </card>
                                    <card plain>
                                        <b>Patient Diagnosis Management</b>
                                        <GSDDDiagnosisComponent :patient="allergy.patient"
                                            v-model="patientDiagnoses[calculatedIndex(index)]" />
                                    </card>
                                    <card plain>
                                        <b>Patient Other Medications Management</b>
                                        <OtherMedicationsComponent :patient="allergy.patient" />
                                    </card>
                                </b-col>
                            </b-row>
                        </card>
                    </b-collapse>
                </div>
                <BottomPagerBar v-model="pageNumber" :total-rows="unprocessedPatientAllergyUpdates.length"
                    :per-page="pageSize" :loading="loading" show-page-size-selector
                    @change-page-size="pageSize = $event" />
            </card>
        </b-col>
    </b-row>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import moment from 'moment';
import SearchComponent from '@/components/SearchComponent.vue';
import PatientAllergyComponent from '@/components/GSDDAllergies/GSDDAllergyComponent.vue';
import { GSDDPatientAllergy, GSDDPatientDiagnosis } from "@/models/GSDD";
import PatientDemographicsComponent from '@/components/Patient/PatientDemographicsComponent.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import GSDDDiagnosisComponent from "@/components/GSDDDiagnosis/GSDDDiagnosisComponent.vue";
import { PatientAllergyUpdate } from "@/models/PatientAllergyUpdate";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import OtherMedicationsComponent from '@/components/Patient/OtherMedicationsComponent.vue';
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';

@Component({
    name: "PatientWebAllergyUpdateQueue",
    components: {
        BottomPagerBar,
        GSDDDiagnosisComponent,
        SearchComponent,
        PatientAllergyComponent,
        PatientDemographicsComponent,
        OtherMedicationsComponent
    },
})

export default class PatientWebAllergyUpdateQueue extends Mixins(AutoModeHotkeyMixin) {
    protected unprocessedPatientAllergyUpdates: PatientAllergyUpdate[] = [];
    protected pageNumber = 1;
    protected pageSize = 50;
    protected loading = false;
    protected visibleAllergies: PatientAllergyUpdate[] = [];

    protected patientsAllergies: GSDDPatientAllergy[][] = [];
    protected patientDiagnoses: GSDDPatientDiagnosis[][] = [];

    async created() {
        this.pageSize = getPageSizePreference(this, 'pageSize_AllergyUpdateQueue', 50);
        await this.getUnprocessedPatientAllergyUpdates();

        const allergyId = this.$route.params.allergyId;
        if (allergyId) {
            await this.loadAndShowItem(parseInt(allergyId));
        }
    }

    @Watch('$route')
    async onRouteChange(newRoute: any, oldRoute: any) {
        if (newRoute.name !== oldRoute.name) {
            return;
        }

        const oldAllergyId = oldRoute.params.allergyId;
        const newAllergyId = newRoute.params.allergyId;

        if (oldAllergyId !== newAllergyId && newAllergyId) {
            console.log('Route allergyId parameter changed, loading new allergy item');
            await this.getUnprocessedPatientAllergyUpdates();
            await this.loadAndShowItem(parseInt(newAllergyId));
        }
    }

    async loadAndShowItem(id: number) {
        try {
            const itemIndex = this.unprocessedPatientAllergyUpdates.findIndex(a => a.id === id);

            if (itemIndex >= 0) {
                const pageNumber = Math.floor(itemIndex / this.pageSize) + 1;
                this.pageNumber = pageNumber;

                const allergyUpdate = this.unprocessedPatientAllergyUpdates[itemIndex];

                if (!this.visibleAllergies.includes(allergyUpdate)) {
                    this.visibleAllergies.push(allergyUpdate);
                }

                await this.$nextTick();
                this.$root.$emit('bv::toggle::collapse', `panel-${allergyUpdate.id}`);
            } else {
                await this.loadSingleItem(id);
            }
        } catch (error) {
            console.error('Error loading and showing allergy item:', error);
            this.$notification(NotificationOptions.error('Failed to load allergy update'));
        }
    }

    async loadSingleItem(id: number) {
        try {
            const response = await Axios.get<PatientAllergyUpdate>(`/PatientAllergyUpdate/${id}`);
            const allergyUpdate = new PatientAllergyUpdate(response.data);

            if (allergyUpdate) {
                this.unprocessedPatientAllergyUpdates.unshift(allergyUpdate);
                this.pageNumber = 1;
                this.visibleAllergies.push(allergyUpdate);

                await this.$nextTick();
                this.$root.$emit('bv::toggle::collapse', `panel-${allergyUpdate.id}`);
            }
        } catch (error) {
            console.error('Error loading single allergy update:', error);
            this.$notification(NotificationOptions.error('Failed to load allergy update'));
        }
    }

    @Watch("pageSize")
    @Watch("sortBy")
    @Watch("sortDesc")
    resetPageNumber() {
        this.pageNumber = 1;
    }

    @Watch("pageSize")
    onPageSizePreferenceChanged() {
        savePageSizePreference(this, 'pageSize_AllergyUpdateQueue', this.pageSize);
    }

    @Watch("unprocessedPatientAllergyUpdates")
    resetAllergies() {
        this.patientsAllergies = this.unprocessedPatientAllergyUpdates.map(() => []);
        this.patientDiagnoses = this.unprocessedPatientAllergyUpdates.map(() => []);
    }

    get filteredAllergyUpdates() {
        const start = (this.pageNumber - 1) * this.pageSize;
        const end = this.pageNumber * this.pageSize;
        return this.unprocessedPatientAllergyUpdates.slice(start, end);
    }

    async getUnprocessedPatientAllergyUpdates() {
        try {
            this.loading = true;
            const response = await Axios.get<PatientAllergyUpdate[]>(`/PatientAllergyUpdate/GetUnprocessed`);
            this.unprocessedPatientAllergyUpdates = response.data.map(pa => new PatientAllergyUpdate(pa));
        } catch (error) {
            console.warn(error);
        } finally {
            this.loading = false;
        }
    }

    calculatedIndex(index: number) {
        return (this.pageSize * (this.pageNumber - 1)) + index;
    }

    shouldLoad(allergy: PatientAllergyUpdate): boolean {
        return this.visibleAllergies.includes(allergy);
    }

    toggleInfo(allergy: PatientAllergyUpdate) {
        if (!this.shouldLoad(allergy)) this.visibleAllergies.push(allergy);
        this.$root.$emit('bv::toggle::collapse', `panel-${allergy.id}`);
    }

    async processAllergy(allergyList: GSDDPatientAllergy[], patWebAllergyId: number) {
        try {
            if (allergyList.length) {
                await Axios.post(`/Allergy/SaveGSDDAllergies/${allergyList[0].patientId}`, allergyList);
            }
            await Axios.post(`PatientAllergyUpdate/ProcessPatientAllergy/${patWebAllergyId}`);
            await this.getUnprocessedPatientAllergyUpdates();

            await this.handleAutoModeCompletion();
        } catch (error) {
            console.error(error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async handleAutoModeCompletion() {
        await this.handleAutoModeNextItem();
    }

    getAge(date: Date) {
        return moment.utc(date).local().fromNow();
    }
}
</script>
